<template>
  <div>
    <label v-if="props.withLabel" class="h6 text-muted">
      {{ $t("common.company_search.label") }}
    </label>
    <p v-if="props.withDescription" class="text-muted small">
      {{ $t("common.company_search.description") }}
    </p>

    <SearchableSelectComponent
      :options="combinedOptions"
      :modelValue="selectedValue"
      :placeholder="placeholder || $t('common.company_search.placeholder')"
      :isLoading="isLoadingCompanies"
      :isError="hasCompanyError"
      :errorMessage="
        isSearchUnavailable
          ? $t('common.company_search.unavailable')
          : undefined
      "
      :invalid="props.invalid"
      @update:modelValue="handleSelectCompany"
      @queryUpdated="handleQueryUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import {
  isCompanySearchUnavailable,
  type CoreSignalCompany,
} from "@/composables/coreSignalCompanyApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { debounce, filter, join } from "lodash-es";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    countryCode?: string;
    placeholder?: string;
    withLabel?: boolean;
    withDescription?: boolean;
    modelValue?: string;
    invalid?: boolean;
  }>(),
  {
    withLabel: true,
    withDescription: true,
  }
);

const { t } = useI18n();

const emit = defineEmits<{
  select: [value: CoreSignalCompany];
  "update:modelValue": [value: string];
  input: [value: string];
}>();

const coreSignalCompanies = ref<CoreSignalCompany[]>([]);
const isLoadingCompanies = ref(false);
const hasCompanyError = ref(false);
const isSearchUnavailable = ref(false);
const searchQuery = ref("");
let currentAbortController: AbortController | null = null;

const { globalErrorHandler } = useGlobalHandlers();

const fetchCoreSignalCompanies = async (query = "") => {
  if (query && query.length < 2) {
    // Too short to search, so no earlier search describes the field any more: cancel one still in
    // flight, which during an outage can hang for seconds and then report its failure.
    currentAbortController?.abort();
    coreSignalCompanies.value = [];
    isLoadingCompanies.value = false;
    hasCompanyError.value = false;
    isSearchUnavailable.value = false;
    return;
  }

  if (currentAbortController) {
    currentAbortController.abort();
  }

  currentAbortController = new AbortController();
  const requestController = currentAbortController;
  isLoadingCompanies.value = true;
  hasCompanyError.value = false;
  isSearchUnavailable.value = false;

  try {
    const response = await goSolidApi.get("/api/utils/coresignal/companies", {
      params: { search: query, country_code: props.countryCode, limit: 10 },
      signal: requestController.signal,
    });
    coreSignalCompanies.value = response.data;
  } catch (error: any) {
    if (error.name === "CanceledError" || error.name === "AbortError") {
      return;
    }
    // Search runs on every pause in typing, so a CoreSignal outage would raise the global error
    // toast once per keystroke. The dropdown says it once, in place, and the typed name stays the
    // field's value for the user to complete the form by hand.
    if (isCompanySearchUnavailable(error)) {
      isSearchUnavailable.value = true;
    } else {
      globalErrorHandler(error);
    }
    hasCompanyError.value = true;
    coreSignalCompanies.value = [];
  } finally {
    if (currentAbortController === requestController) {
      isLoadingCompanies.value = false;
    }
  }
};

const debouncedFetchCompanies = debounce(fetchCoreSignalCompanies, 500);

watch(searchQuery, (newQuery) => {
  debouncedFetchCompanies(newQuery);
});

const updateCoreSignalCompanySearchQuery = (query: string) => {
  searchQuery.value = query;
};

onBeforeUnmount(() => {
  if (currentAbortController) {
    currentAbortController.abort();
  }
  debouncedFetchCompanies.cancel();
});

const companyOptions = computed(() =>
  coreSignalCompanies.value.map((company) => {
    const descriptionParts = filter(
      [
        company.industry,
        company.country && !props.countryCode ? company.country : null,
        company.website,
      ],
      Boolean
    );

    return {
      label: company.name,
      value: String(company.id),
      description: join(descriptionParts, " | "),
      _company: company,
    };
  })
);

const customEntryOption = computed(() => {
  let customText = null;
  let description = "";

  if (props.modelValue) {
    const matchingOption = companyOptions.value.find(
      (option) => option.label === props.modelValue
    );
    if (!matchingOption) {
      customText = props.modelValue;
      description = t("common.company_search.custom_entry");
    }
  }

  if (customText) {
    return {
      label: customText,
      value: "custom_entry",
      description,
      _isCustomEntry: true,
      _company: {
        id: 0,
        name: customText,
        website: null,
        industry: null,
        description: null,
        founded: null,
        size: null,
        type: null,
        country: null,
        locality: null,
        region: null,
        street_address: null,
        postal_code: null,
        professional_network_url: null,
      } as CoreSignalCompany,
    } as any;
  }

  return null;
});

const combinedOptions = computed(() => {
  const options = [...companyOptions.value];
  if (customEntryOption.value) {
    options.unshift(customEntryOption.value);
  }
  return options;
});

const selectedValue = computed(() => {
  if (customEntryOption.value) {
    return "custom_entry";
  }

  if (props.modelValue) {
    const matchingOption = companyOptions.value.find(
      (option) => option.label === props.modelValue
    );
    if (matchingOption) {
      return matchingOption.value;
    }
  }

  return undefined;
});

const handleSelectCompany = async (id: string) => {
  if (id === "custom_entry") {
    const customText = customEntryOption.value?.label || "";
    emit("update:modelValue", customText);
    return;
  }

  const selectedOption = companyOptions.value.find(
    (option) => option.value === id
  );
  if (selectedOption && selectedOption._company) {
    const company = selectedOption._company;
    emit("select", company);
    emit("update:modelValue", company.name);
    updateCoreSignalCompanySearchQuery("");
  } else {
    emit("update:modelValue", id);
  }
};

const handleQueryUpdated = (query: string) => {
  updateCoreSignalCompanySearchQuery(query);
  emit("input", query);
  emit("update:modelValue", query);
};
</script>
